/**
 * Capacitor permission helpers — request OS permissions at the moment a
 * feature is actually invoked, not at app launch. Apple App Review reliably
 * rejects apps that ask for permissions up front.
 *
 * All helpers are SAFE on web:
 *   - On iOS / Android (Capacitor native shell), they call the platform plugin.
 *   - On web, they fall back to the browser API (navigator.geolocation) which
 *     prompts the user via the browser's own permission UI.
 *
 * Returned shape (consistent across platforms):
 *   { state: 'granted' | 'denied' | 'prompt' | 'unknown', requested: bool }
 *
 * On denial, callers should show a toast with an "Open Settings" action so the
 * user can re-enable the permission from outside the app — once a user has
 * permanently denied a permission, the system prompt is suppressed forever.
 */
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Geolocation } from '@capacitor/geolocation';

export const isNative = () => Capacitor.isNativePlatform();

/**
 * True when the app is running inside a Median.co (formerly GoNative) native
 * wrapper. Median injects `window.median` at runtime and also stamps its brand
 * in the User-Agent — we check both for robustness across Median SDK versions.
 * Median.co uses a plain iOS WKWebView / Android WebView (no Capacitor bridge),
 * so `isNative()` above returns false inside it — this helper exists so we can
 * still adapt UX (e.g. use a redirect-based OAuth flow instead of the GIS iframe
 * which refuses to render for non-http origins).
 */
export const isMedianWebView = () => {
  if (typeof window === 'undefined') return false;
  if (window.median || window.gonative) return true;
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  return /median|gonative/i.test(ua);
};

/**
 * True in any native/WebView wrapper (Capacitor OR Median). Use this when a
 * behaviour should differ from a regular desktop/mobile browser — most notably
 * the Google Sign-In flow, which can't use the GIS iframe widget inside a
 * WebView because the origin isn't whitelistable in Google Cloud Console.
 */
export const isWebViewApp = () => isNative() || isMedianWebView();

/**
 * Open the OS app-settings screen so the user can flip the permission toggle.
 * iOS: `app-settings:` URL deep-link.
 * Android: `App.openUrl` opens the device Settings → Apps → TheUnityBlaze page.
 * Web: no-op (browser permission settings are per-site, accessible from the
 * address-bar lock icon — out of our reach programmatically).
 */
export async function openAppSettings() {
  if (!isNative()) return false;
  const platform = Capacitor.getPlatform();
  try {
    if (platform === 'ios') {
      await CapacitorApp.openUrl({ url: 'app-settings:' });
    } else if (platform === 'android') {
      // Generic Android intent — opens the app's settings page.
      await CapacitorApp.openUrl({ url: 'package:com.unityblaze.connection' });
    }
    return true;
  } catch (e) {
    console.warn('openAppSettings failed:', e);
    return false;
  }
}

/**
 * Ensure foreground location permission is granted. If permission is in the
 * `prompt` state, requests it now (this fires the system dialog).
 *
 * Returns `{ state: 'granted' | 'denied' | 'prompt' | 'unknown' }`.
 * Web fallback: returns 'unknown' (the browser handles its own prompting
 * inside `getCurrentPosition`).
 */
export async function ensureLocationPermission() {
  if (!isNative()) return { state: 'unknown' };

  try {
    let status = await Geolocation.checkPermissions();
    if (status.location === 'prompt' || status.location === 'prompt-with-rationale') {
      status = await Geolocation.requestPermissions({ permissions: ['location'] });
    }
    return { state: status.location };
  } catch (e) {
    console.warn('ensureLocationPermission failed:', e);
    return { state: 'unknown' };
  }
}

/**
 * Get the current foreground position. Handles permission flow first.
 * Returns `{ coords: {lat,lng}, source: 'native'|'web' } | { error: '...' }`.
 *
 * Timeouts: 12 s on native, 15 s on web (browsers can be slow on first use).
 */
export async function getCurrentPosition() {
  if (isNative()) {
    const perm = await ensureLocationPermission();
    if (perm.state !== 'granted' && perm.state !== 'unknown') {
      return { error: 'denied' };
    }
    try {
      const pos = await Geolocation.getCurrentPosition({
        enableHighAccuracy: false,
        timeout: 12000,
        maximumAge: 60000,
      });
      return {
        coords: { lat: pos.coords.latitude, lng: pos.coords.longitude },
        source: 'native',
      };
    } catch (e) {
      console.warn('Geolocation native error:', e);
      // iOS throws when user taps "Don't Allow" — surface as denied so caller
      // can show the unified denied-fallback UI.
      const message = String(e?.message || e);
      if (/denied|permission/i.test(message)) return { error: 'denied' };
      return { error: 'unavailable' };
    }
  }

  // Web fallback
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return { error: 'unsupported' };
  }
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          coords: { lat: pos.coords.latitude, lng: pos.coords.longitude },
          source: 'web',
        }),
      (err) => {
        if (err.code === err.PERMISSION_DENIED) resolve({ error: 'denied' });
        else if (err.code === err.TIMEOUT) resolve({ error: 'timeout' });
        else resolve({ error: 'unavailable' });
      },
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
    );
  });
}
